import React, { useEffect, useRef } from 'react';
import { Select, Row, Col, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { ViewMode } from './TaskMonitoringFilter';
import { DriverFilterOption } from '../../../../../utils/driverFilterOption';

const { Option } = Select;

/** 远程搜索防抖间隔，与 components/SearchSelect 默认值保持一致 */
const SEARCH_DEBOUNCE_MS = 300;

export interface DetailFilters {
    selectedWarehouses?: string[];
    selectedDsps: string[];
    selectedAreas: string[];
    /** 司机 accountId 字符串数组（不是姓名，姓名允许重名，见 utils/driverFilterOption） */
    selectedDrivers: string[];
    selectedStatusTypes?: string[];
}

interface TaskDetailFilterProps {
    filters: DetailFilters;
    /** 支持传入完整对象或局部字段；父组件应使用 setDetailFilters(prev => ({ ...prev, ...next })) 合并，避免清空一个下拉时其它字段被误覆盖 */
    onFiltersChange: (next: DetailFilters | Partial<DetailFilters>) => void;
    warehouses?: Array<{ id: string; name: string }>;
    dsps: Array<{ id: number; name: string }>;
    areas: Array<{ id: number; name: string }>;
    drivers: DriverFilterOption[];
    viewMode: ViewMode;
    showWarehouseSelect?: boolean;
    disableWarehouseSelect?: boolean;
    showDspSelect?: boolean;
    showAreaSelect?: boolean;
    showDriverSelect?: boolean;
    statusTypeOptions?: Array<{ value: string; label: string }>;
    selectedStatusTypes?: string[];
    onStatusTypesChange?: (statusTypes: string[]) => void;
    noWrapper?: boolean; // 是否不包含外层包装div
    currentDspId?: number; // 当前DSP ID（DSP经理角色）
    dspMode?: boolean; // 是否为DSP经理
    /** 司机远程搜索；传入时司机下拉候选由后端按关键词返回，需关闭本地过滤 */
    onDriverSearch?: (keyword: string) => void;
    driverOptionsLoading?: boolean;
    /** 区域远程搜索；Hub 页顶部无区域下拉，只能由抽屉驱动关键词，故必须支持 */
    onAreaSearch?: (keyword: string) => void;
    areaOptionsLoading?: boolean;
}

const TaskDetailFilter: React.FC<TaskDetailFilterProps> = ({
    filters,
    onFiltersChange,
    warehouses = [],
    dsps,
    areas,
    drivers,
    viewMode,
    showWarehouseSelect = false,
    disableWarehouseSelect = false,
    showDspSelect = true,
    showAreaSelect = true,
    showDriverSelect = true,
    statusTypeOptions,
    selectedStatusTypes,
    onStatusTypesChange,
    noWrapper = false,
    currentDspId,
    dspMode = false,
    onDriverSearch,
    driverOptionsLoading,
    onAreaSearch,
    areaOptionsLoading
}) => {
    const { t } = useTranslation();

    // 远程搜索逐字符请求会打满后端，与 components/SearchSelect 一致做 300ms 防抖。
    // 区域与司机同屏渲染，按字段各自计时，避免相互取消掉待发请求。
    const searchDebounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    useEffect(() => {
        const timers = searchDebounceTimers.current;
        return () => timers.forEach((timer) => clearTimeout(timer));
    }, []);
    const withSearchDebounce = (field: string, handler?: (keyword: string) => void) => {
        if (!handler) return undefined;
        return (keyword: string) => {
            const pending = searchDebounceTimers.current.get(field);
            if (pending) clearTimeout(pending);
            searchDebounceTimers.current.set(field, setTimeout(() => handler(keyword), SEARCH_DEBOUNCE_MS));
        };
    };
    const handleDriverSearch = withSearchDebounce('driver', onDriverSearch);
    const handleAreaSearch = withSearchDebounce('area', onAreaSearch);

    // 只在下拉收起时把关键词清回空，下次点开是范围内的默认列表。
    // 选中项时不能清：antd 多选选中后会自行清空输入框文本但不回调 onSearch，
    // 此时若跟着把关键词也清掉，候选会立刻回落成默认前 50 条，
    // 用户没法在同一批搜索结果里连续勾选（输入 f 选中 F052 后就看不到 F054 了）。
    const resetSearchKeyword = (field: string, handler?: (keyword: string) => void) => {
        if (!handler) return;
        const pending = searchDebounceTimers.current.get(field);
        if (pending) clearTimeout(pending);
        handler('');
    };
    const withKeywordResetOnClose = (field: string, handler?: (keyword: string) => void) => {
        if (!handler) return undefined;
        return (open: boolean) => {
            if (!open) resetSearchKeyword(field, handler);
        };
    };

    // 所有筛选（WHS / DSP / 区域 / 司机 / 状态）统一使用 1/4 宽度（span=6）；
    // 一行最多 4 个，第 5 个自动换到第二行，布局与 hub-parcel-sla-monitoring 保持一致。
    const selectSpan = 6;

    const content = (
        <Row gutter={[16, 16]} align="middle">
            {showWarehouseSelect && (
                <Col span={selectSpan}>
                    <Select
                        mode="multiple"
                        value={filters.selectedWarehouses || []}
                        onChange={(value) => onFiltersChange({ selectedWarehouses: value })}
                        style={{ width: '100%' }}
                        placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.whs')}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            String(option?.label ?? option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {warehouses.map((whs) => (
                            <Option key={whs.id} value={whs.name}>{whs.name}</Option>
                        ))}
                    </Select>
                </Col>
            )}
            {showDspSelect && (
                <Col span={selectSpan}>
                    <Select
                        mode="multiple"
                        value={filters.selectedDsps}
                        onChange={(value) => onFiltersChange({ selectedDsps: value })}
                        style={{ width: '100%' }}
                        placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.dsp')}
                        allowClear={!dspMode}
                        showSearch
                        filterOption={(input, option) =>
                            String(option?.label ?? option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        disabled={dspMode}
                    >
                        {dsps.map((dsp) => (
                            <Option key={dsp.id} value={dsp.name}>{dsp.name}</Option>
                        ))}
                    </Select>
                </Col>
            )}
            {showAreaSelect && (
                <Col span={selectSpan}>
                    <Select
                        mode="multiple"
                        value={filters.selectedAreas}
                        onChange={(value) => {
                            onFiltersChange({ selectedAreas: value });
                        }}
                        style={{ width: '100%' }}
                        placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.area')}
                        allowClear
                        showSearch
                        onSearch={handleAreaSearch}
                        onDropdownVisibleChange={withKeywordResetOnClose('area', onAreaSearch)}
                        loading={areaOptionsLoading}
                        filterOption={
                            handleAreaSearch
                                ? false
                                : (input, option) =>
                                    String(option?.label ?? option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {areas.map((area) => (
                            <Option key={area.id} value={area.name}>{area.name}</Option>
                        ))}
                    </Select>
                </Col>
            )}
            {showDriverSelect && (
                <Col span={selectSpan}>
                    <Select
                        mode="multiple"
                        value={filters.selectedDrivers}
                        onChange={(value) => {
                            onFiltersChange({ selectedDrivers: value });
                        }}
                        style={{ width: '100%' }}
                        placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.driver')}
                        allowClear
                        showSearch
                        onSearch={handleDriverSearch}
                        onDropdownVisibleChange={withKeywordResetOnClose('driver', onDriverSearch)}
                        loading={driverOptionsLoading}
                        filterOption={
                            handleDriverSearch
                                ? false
                                : (input, option) =>
                                    String(option?.label ?? option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {drivers.map(driver => (
                            <Option key={driver.accountId} value={String(driver.accountId)} title={driver.title}>{driver.name}</Option>
                        ))}
                    </Select>
                </Col>
            )}
            {statusTypeOptions && statusTypeOptions.length > 0 && onStatusTypesChange && (
                <Col span={selectSpan}>
                    <Select
                        mode="multiple"
                        value={selectedStatusTypes ?? []}
                        onChange={(value) => onStatusTypesChange(value)}
                        style={{ width: '100%' }}
                        placeholder={t('wms.delivery.dailyTaskMonitoring.detailColumns.status')}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            String(option?.label ?? option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {statusTypeOptions.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))}
                    </Select>
                </Col>
            )}
        </Row>
    );

    if (noWrapper) {
        return content;
    }

    return (
        <div className="mb-4 pb-4 border-b">
            {content}
        </div>
    );
};

export default TaskDetailFilter;
